/**
 * Infinite Scroll Pagination
 *
 * Automatically loads the next page of listing results when the user
 * scrolls near the bottom. Fetches the next page via AJAX, extracts
 * the office cards, and appends them to the current results.
 */

const TRIGGER_DISTANCE = 600;
const LOADING_DISPLAY_MS = 1500;

let isLoading = false;
let nextPageUrl = null;
let resultsContainer = null;
let statusContainer = null;
let loadingEl = null;

export function initInfiniteScroll() {
    if (document.body.id !== 'listing') return;

    resultsContainer = document.querySelector('.listing-results');
    const paginationContainer = document.querySelector('.listing-pagination');
    if (!resultsContainer || !paginationContainer) return;

    // Find the next page URL from the server-rendered pagination link
    const nextLink = paginationContainer.querySelector('.listing-pag-next');
    nextPageUrl = nextLink?.getAttribute('href') || null;
    if (!nextPageUrl) return; // Single page — no infinite scroll needed

    // Remove the entire server-rendered pagination — we take over from here
    paginationContainer.remove();

    // Create our own status container after the results
    statusContainer = document.createElement('div');
    statusContainer.className = 'listing-infinite-status';
    resultsContainer.parentNode.insertBefore(statusContainer, resultsContainer.nextSibling);

    // Build the loading indicator
    loadingEl = document.createElement('div');
    loadingEl.className = 'listing-pagination-loading';
    loadingEl.innerHTML = '<img src="/img/logoAnimated.gif" alt="Loading" width="40" height="58"><span>Loading more offices...</span>';
    statusContainer.appendChild(loadingEl);

    window.addEventListener('scroll', onScroll, { passive: true });
}

function onScroll() {
    if (isLoading || !nextPageUrl) return;

    const scrollBottom = window.scrollY + window.innerHeight;
    const docHeight = document.documentElement.scrollHeight;

    if (scrollBottom < docHeight - TRIGGER_DISTANCE) return;

    loadNextPage();
}

async function loadNextPage() {
    if (!nextPageUrl) {
        exhaust();
        return;
    }

    isLoading = true;
    loadingEl.classList.add('is-visible');

    const loadStartTime = Date.now();

    try {
        const response = await fetch(nextPageUrl, {
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
                'Accept': 'text/html',
            },
            credentials: 'same-origin',
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        // Extract new office cards
        const newCards = doc.querySelectorAll('.listing-results .office-card--listing');

        // Extract next page URL from the fetched page's pagination
        const newNextLink = doc.querySelector('.listing-pag-next');
        nextPageUrl = newNextLink?.getAttribute('href') || null;

        // Ensure loading shows for minimum time
        const elapsed = Date.now() - loadStartTime;
        if (elapsed < LOADING_DISPLAY_MS) {
            await new Promise(r => setTimeout(r, LOADING_DISPLAY_MS - elapsed));
        }

        loadingEl.classList.remove('is-visible');

        if (!newCards.length) {
            exhaust();
            return;
        }

        // Append cards to results
        newCards.forEach(card => {
            card.addEventListener('click', e => {
                if (e.target.closest('.listing-shortlist-btn')) return;
                if (e.target.closest('[data-map-link]')) return;
                window.location.href = card.dataset.href;
            });
            resultsContainer.appendChild(card);
        });

        // Handle broken images on new cards
        newCards.forEach(card => {
            card.querySelectorAll('img.office-card-image').forEach(img => {
                img.addEventListener('error', () => img.classList.add('is-broken'));
                if (img.complete && img.naturalWidth === 0) img.classList.add('is-broken');
            });
        });

        // Check if there are more pages
        if (!nextPageUrl) {
            exhaust();
        } else {
            isLoading = false;
        }

        updateListingSchema();

    } catch (err) {
        console.warn('[infinite-scroll] Failed to load next page:', err);
        loadingEl.classList.remove('is-visible');
        // Show a text fallback on error
        loadingEl.innerHTML = '<span>Failed to load more offices. <a href="' + nextPageUrl + '">View next page</a></span>';
        loadingEl.classList.add('is-visible');
        isLoading = false;
    }
}

function exhaust() {
    nextPageUrl = null;
    isLoading = false;
    window.removeEventListener('scroll', onScroll);

    const totalCards = resultsContainer.querySelectorAll('.office-card--listing').length;
    statusContainer.innerHTML = '';
    const msg = document.createElement('p');
    msg.className = 'listing-all-loaded';
    msg.textContent = `All offices loaded, ${totalCards} found`;
    statusContainer.appendChild(msg);
}

function updateListingSchema() {
    const existing = document.querySelector('script[type="application/ld+json"]');
    if (existing && existing.textContent.includes('ItemList')) {
        existing.remove();
    }

    const cards = document.querySelectorAll('.office-card--listing[data-href][data-office-name]');
    if (!cards.length) return;

    const items = [];
    cards.forEach((card, i) => {
        items.push({
            '@type': 'ListItem',
            position: i + 1,
            url: window.location.origin + card.dataset.href,
            name: card.dataset.officeName,
        });
    });

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: document.querySelector('.listing-page-title')?.textContent?.trim() || 'Serviced Offices',
        url: window.location.href.split('?')[0],
        numberOfItems: items.length,
        itemListOrder: 'https://schema.org/ItemListUnordered',
        itemListElement: items,
    });
    document.head.appendChild(script);
}
